import { z } from "zod";

export type ID = {
    id: string | number;
}

export type Artifact = ID & {
    kind: 'article' | 'summary' | 'highlight' | 'form' | 'profile';
}

export type Article = Artifact & {
    meta: {
        title: string;
        description: string;
    }
    title: string;
    content: string;
    image: string;
}

export type Summary = Artifact & {
    content: string;
}

export type Highlight = Artifact & {
    content: string;
    description?: string;
}

export type Form = Artifact & {
    name: string;
    values: Record<string, any>;
}

export type Profile = Artifact & {
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
}

export type Message = ID & {
    role: 'user' | 'agent';
    content: string | Article | Summary | Highlight | Form | Profile;
}

export const ProposalForm = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.email("Valid email is required"),
    company: z.string().min(3, "Company name is required"),
    website: z.hostname("Valid website is required"),
    lines: z.number().min(1, "Minimum number is 1"),
    agents: z.number().min(1, "Minimum number is 1"),
    agentHourlyRate: z.number().min(10, "Minimum number is 10"),
    monthlyCalls: z.number().min(1, "Minimum number is 1"),
    monthlyMissedCalls: z.number().min(0, "Minimum number is 0"),
    monthlyMinutes: z.number().min(1, "Minimum number is 1"),
    cpl: z.number().min(0, "Minimum number is 0"),
    averageServicePrice: z.number().min(0, "Minimum number is 0"),
    primaryGoal: z.string().min(1, "Primary goal is required"),
    additionalInfo: z.string().optional().or(z.literal('')),
    submittedAt: z.string().optional(),
    // countryCode: z.string().min(1, "Country code is required"),
});

export const SupportForm = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.email("Valid email is required"),
    company: z.string(),
    category: z.string().min(1, "Support category is required"),
    organizationId: z.uuid("Valid organization ID is required").or(z.literal('')),
    message: z.string().min(50, "Message must be at least 50 characters long"),
});

export const NewsletterJoinForm = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.email("Valid email is required"),
    company: z.string(),
});

export const NewsletterLeaveForm = z.object({
    email: z.email("Valid email is required"),
    code: z.guid("Valid leave code is required"),
    reason: z.string(),
});

export type ProposalForm = z.infer<typeof ProposalForm>;
export type SupportForm = z.infer<typeof SupportForm>;
export type NewsletterJoinForm = z.infer<typeof NewsletterJoinForm>;
export type NewsletterLeaveForm = z.infer<typeof NewsletterLeaveForm>;